import { useEffect, useRef, useState } from 'react';
import { useAppDispatch } from '../../common/hooks/useAppDispatch';
import { useAppSelector } from '../hooks/useAppSelector';
import {
  updateEmployee,
  setEditingEmployee,
  updateEditingEmployee,
  clearEditingEmployee,
  selectEditingEmployee,
  selectEmployeesStatus,
} from '../../slices/employees/employeesSlice';
import { Employee } from '../../models/employee.model';
import { isEmailEmpty, isValidEmail } from '../utils/email';

import { hideOverlay, showOverlay } from '../../slices/show-progress-overlay/showProgressOverlay';

export function useEmployeeDetailsCardForm(employee: Partial<Employee>) {
  const dispatch = useAppDispatch();
  const [showModal, setShowModal] = useState(false);
  const [showAddSocialModal, setShowAddSocialModal] = useState(false);
  const formRef = useRef<HTMLDivElement | null>(null);

  // Get editing state from Redux
  const editingEmployee = useAppSelector(selectEditingEmployee);
  const status = useAppSelector(selectEmployeesStatus);
  const isEdited = !!editingEmployee && editingEmployee.id === employee.id;

  // Initialize the editing employee in Redux if needed
  useEffect(() => {
    if (employee && !editingEmployee && isEdited) {
      dispatch(setEditingEmployee(employee));
    }
  }, [employee, editingEmployee, dispatch, isEdited]);

  const handleChange =
    <K extends keyof Employee>(field: K) =>
    (value: Employee[K]) => {
      if (isEdited) {
        dispatch(
          updateEditingEmployee({
            field,
            value,
          })
        );
      }
    };

  // Both email fields are optional, but they must hold a valid address when filled in.
  const areEmailsValid = () =>
    [editingEmployee?.email_address, editingEmployee?.business_email_address].every(
      (email) => isEmailEmpty(email) || isValidEmail(email)
    );

  const saveChanges = async () => {
    if (isEdited && editingEmployee) {
      // The email fields show the error inline; block the save on an invalid address.
      if (!areEmailsValid()) return;
      dispatch(showOverlay());
      dispatch(updateEmployee(editingEmployee))
        .unwrap()
        .finally(() => {
          dispatch(hideOverlay());
        });
      setShowModal(false);
    }
  };

  const cancelChanges = () => {
    dispatch(clearEditingEmployee());
  };

  const saveChangesSocialForm = () => {
    toogleEditingSocialModal();
  };

  const toggleEditing = () => {
    if (isEdited) {
      dispatch(clearEditingEmployee());
    } else {
      dispatch(setEditingEmployee(employee));
    }
  };

  const toogleEditingSocialModal = () => {
    setShowAddSocialModal((prev) => !prev);
  };

  const confirmExit = () => {
    dispatch(clearEditingEmployee());
    setShowModal(false);
  };

  // Handle clicks outside the form
  useEffect(() => {
    let showModalTimeout: ReturnType<typeof setTimeout> | undefined;
    const handleClickOutside = (event: MouseEvent) => {
      if (isEdited && formRef.current && !formRef.current.contains(event.target as Node)) {
        if (event.target instanceof HTMLElement) {
          // You can add ignored elements check if needed
        }
        showModalTimeout = setTimeout(() => {
          setShowModal(true);
        }, 0);
      }
    };

    if (isEdited) document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      if (showModalTimeout) clearTimeout(showModalTimeout);
    };
  }, [isEdited]);

  return {
    formData: editingEmployee || employee,
    handleChange,
    saveChanges,
    isEdited,
    toggleEditing,
    formRef,
    showModal,
    setShowModal,
    confirmExit,
    showAddSocialModal,
    setShowAddSocialModal,
    toogleEditingSocialModal,
    saveChangesSocialForm,
    cancelChanges,
    isLoading: status === 'loading',
  };
}
